import { LocalStorageKeys } from "@shared/types/localTypes/storage";
import { useAuthStore } from "@shared/stores/auth";
import { usersService } from "@shared/services/user";
import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import { useAuthorizationStore } from "@shared/stores/authorization";
import { cookiesService } from "@shared/services/cookies";
import { RouteNames } from "@shared/types/localTypes/router";
import { Router } from "vue-router";
import { genericLocalStorage } from "@shared/module-transformers/localStorage.ts";
import { notificationTokenService } from "@shared/services/notificationToken.ts";
import { usePushNotificationsPermissionStore } from "@shared/stores/pushNotificationsPermissionStore.ts";
import { useBootstrapStore } from "@shared/stores/bootstrap.ts";
import { useEngine } from "@shared/use/useEngine.ts";
import { useSupportTicketsStore } from "@shared/stores/supportTickets.ts";
import { analyticsService } from "@shared/services/analytics.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";
import useInAppNotificationsPubSub from "@shared/use/in-app-notifications/useInAppNotificationsPubSub.ts";
import window from "@shared/utils/app-window.ts";

export const appService = {
  homeUrl: () => {
    return window.location.origin;
  },
  clearSessionCache: async () => {
    await genericLocalStorage.clear([
      LocalStorageKeys.FROM_QUERY,
      LocalStorageKeys.LAST_LOGIN_EMAIL,
    ]);
  },
  logOut: async () => {
    const sessionLogic = useSessionLogic();

    await usersService.proxyLogout();
    await useAuthStore().clear();
    useSubscriptionsStore().clearSubscriptions();
    useAuthorizationStore().clearAuthorization();
    useSupportTicketsStore().clearSupportTickets();
    useBootstrapStore().setInitialDataLoaded(false);
    await appService.clearSessionCache();
    await appService.unregisterPushNotificationToken();
    await useInAppNotificationsPubSub().stopInAppNotificationListening();
    analyticsService.clearAll();

    if (useEngine().engine === "WEB") {
      sessionLogic.lockReload();
      useSessionLogic().setLastSessionAction(
        LocalStorageKeys.LAST_SUCCESSFUL_LOGOUT,
      );
      sessionLogic.unlockReloadWithDelay();
    }
  },
  unauthorizedApiInterceptor: async (router: Router) => {
    await appService.logOut();

    if (!useSessionLogic().isLogoutRecentlyClicked()) {
      await cookiesService.setFromCookie();
    }
    await router.push({ name: RouteNames.Login });
  },
  unregisterPushNotificationToken: async () => {
    const token = usePushNotificationsPermissionStore().token;
    if (!token) {
      return;
    }
    await notificationTokenService.unregisterNotificationToken({ token });
  },
  redirectToForbiddenPage: () => {
    window.location.href = `${window.KEYCLOAK_URL}/forbidden?app=${window.KEYCLOAK_CLIENT_ID}`;
  },
};
